import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import {
  Search,
  Filter,
  Download,
  Eye,
  Trash2,
  ArrowUpCircle,
  ArrowDownCircle,
  Bell
} from "lucide-react";
import NotificationStatusBadge from "./NotificationStatusBadge";
import { NotificationContext } from "../context/NotificationContext";
function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

/* Simple generic modal component */
function Modal({ open, title, onClose, children, footer }) {
  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl bg-white rounded-xl shadow-lg overflow-hidden md:rounded-xl">
        <div className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">{title}</h3>
            <button className="text-stone-500 hover:text-stone-700" onClick={onClose} aria-label="Close modal">&times;</button>
          </div>
        </div>

        <div className="p-6 max-h-[70vh] overflow-auto">{children}</div>

        {footer && <div className="px-6 py-4 border-t bg-stone-50 flex justify-end gap-3">{footer}</div>}
      </div>
    </div>
  );
}

export default function NotificationsTable() {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const pageSize = 8;

  // Use state so we can mutate (delete, mark read)
  // const [notifications, setNotifications] = useState([]);

  const { notifications, fetchNotifications, deleteNotification } = useContext(NotificationContext);


  // Modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);

  // Selected notification for view/delete actions
  const [selectedNotification, setSelectedNotification] = useState(null);

  // Export option state
  const [exportFormat, setExportFormat] = useState("csv");
  const [exporting, setExporting] = useState(false);

const filtered = useMemo(() => {
  const q = query.trim().toLowerCase();

  return notifications.filter((n) => {

    const status = (n.status || "").toLowerCase();

    /* STATUS FILTER */
    if (statusFilter === "unread") {
      if (status === "read") return false;
    } 
    else if (statusFilter !== "all") {
      if (status !== statusFilter) return false;
    }

    /* SEARCH */
   if (!q) return true;

const safe = (val) => String(val ?? "").toLowerCase();


  return (
  safe(n.id).includes(q) ||
  safe(n.title).includes(q) ||
  safe(n.message).includes(q) ||
  safe(n.customer).includes(q) ||
  safe(n.email).includes(q) ||
  safe(n.first_name).includes(q) ||
  safe(n.last_name).includes(q) ||
  safe(n.user_email).includes(q)
);
  });

}, [notifications, query, statusFilter]);
  

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const visible = filtered.slice((page - 1) * pageSize, page * pageSize);

  // Stats derived from state
  const stats = useMemo(() => ({
    total: notifications.length,
    unread: notifications.filter((t) => t.status !== "read").length,
    newCount: notifications.filter((t) => t.status === "new").length
  }), [notifications]);

  // Handlers
  function openView(n) {
    setSelectedNotification(n);
    setViewModalOpen(true);
    if (n.status !== "read") markAsRead(n.id);
  }

  function openDelete(n) {
    setSelectedNotification(n);
    setDeleteModalOpen(true);
  }

  async function confirmDelete() {
  if (!selectedNotification) return;
  await deleteNotification(selectedNotification.id); // context handles state update
  setDeleteModalOpen(false);
  setSelectedNotification(null);
}


  function markAsRead(id) {
  fetchNotifications(); // refetch after marking as read from API
}

  function handleExportOpen() {
    setExportFormat("csv");
    setDownloadModalOpen(true);
  }

  function downloadCSV(data, filename = "notifications.csv") {
    const header = ["id","title","message","customer","email","status","create_audit_time"];
    const rows = data.map((d) => header.map((h) => {
      const val = d[h] ?? "";
      return `"${String(val).replace(/"/g, '""')}"`;
    }).join(","));
    const csv = [header.join(","), ...rows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function downloadJSON(data, filename = "notifications.json") {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function confirmExport() {
    setExporting(true);
    try {
      const dataToExport = filtered;
      await new Promise((r) => setTimeout(r, 350));
      if (exportFormat === "csv") downloadCSV(dataToExport);
      else downloadJSON(dataToExport);
      setDownloadModalOpen(false);
    } catch (err) {
      console.error("Export failed", err);
    } finally {
      setExporting(false);
    }
  }

  function handleMarkRead(n) {
    if (n) markAsRead(n.id);
  }

  useEffect(() => setPage(1), [query, statusFilter]);

  // Get most recent create_audit_time for "Last update" card
  const lastUpdate = useMemo(() => {
    if (!notifications.length) return null;
    return notifications.slice().sort((a, b) => new Date(b.create_audit_time) - new Date(a.create_audit_time))[0].create_audit_time;
  }, [notifications]);

  return (
    <div className="p-4 md:p-6">
      <header className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
       <div className="flex items-center gap-4 mb-6">
  <div className="w-12 h-12 bg-teal-700 rounded-xl flex items-center justify-center shadow-sm">
    <Bell className="w-6 h-6 text-white" />
  </div>

  <div>
    <h1 className="text-2xl sm:text-3xl font-bold text-teal-800 leading-tight">
      Notifications
    </h1>
    <p className="text-sm text-stone-500">
      Monitor and manage all system notifications
    </p>
  </div>
</div>

        {/* <div className="flex items-center gap-3 ml-auto">
          <button
            onClick={handleExportOpen}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg shadow-sm hover:bg-emerald-700"
          >
            <Download size={16} /> Export
          </button>
        </div> */}
      </header>

      {/* responsive stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border rounded-lg p-4 shadow-sm">
          <div className="text-sm text-stone-500">Total Notifications</div>
          <div className="text-2xl font-bold text-emerald-800">{stats.total}</div>
        </div>
        <div className="bg-white border rounded-lg p-4 shadow-sm">
          <div className="text-sm text-stone-500">Unread</div>
          <div className="text-2xl font-bold text-amber-700">{stats.unread}</div>
        </div>
        <div className="bg-white border rounded-lg p-4 shadow-sm">
          <div className="text-sm text-stone-500">New</div>
          <div className="text-2xl font-bold text-teal-700">{stats.newCount}</div>
        </div>
        <div className="bg-white border rounded-lg p-4 shadow-sm flex items-center justify-between">
          <div>
            <div className="text-sm text-stone-500">Last update</div>
            <div className="text-sm text-stone-700">{lastUpdate ? formatDate(lastUpdate) : "—"}</div>
          </div>
          <div className="rounded-full p-3 bg-stone-50 shadow-inner">
            <ArrowUpCircle size={28} />
          </div>
        </div>
      </div>

      {/* search + filters */}
      <div className="bg-white border rounded-xl p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="flex-1 w-full">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-300" size={16} />
              <input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                placeholder="Search by ID, title, customer, or email..."
                className="w-full pl-12 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-200"
              />
            </div>
          </div>

          <div className="flex gap-2 items-center w-full md:w-auto">
            {/* <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="px-4 py-2 border rounded-lg w-full md:w-auto"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="unread">Unread</option>
              <option value="read">Read</option>
              <option value="archived">Archived</option>
            </select> */}

            {/* <button className="inline-flex items-center gap-2 px-4 py-2 border rounded-lg">
              <Filter size={16} /> Filters
            </button> */}

            <button onClick={handleExportOpen} className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg">
              <Download size={16} /> Export
            </button>
          </div>
        </div>
      </div>

      {/* TABLE for md+ screens */}
      <div className="hidden md:block bg-white border rounded-xl p-1 overflow-x-auto">
        <table className="min-w-full divide-y">
          <thead className="bg-stone-50">
            <tr>
              <th className="px-6 py-3 text-left text-sm text-stone-500">Notification</th>
              <th className="px-6 py-3 text-left text-sm text-stone-500">Sender / Target</th>
              <th className="px-6 py-3 text-left text-sm text-stone-500">Status</th>
              <th className="px-6 py-3 text-left text-sm text-stone-500">Date</th>
              <th className="px-6 py-3 text-right text-sm text-stone-500">Actions</th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y">
            {visible.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-stone-400">No notifications found.</td>
              </tr>
            )}

            {visible.map((n) => (
              <tr key={n.id} className="hover:bg-stone-50">
                <td className="px-6 py-4 align-top">
                  <div className="text-sm font-semibold text-stone-700">{n.title}</div>
                  <div className="text-xs text-stone-500 mt-1">{n.message}</div>
                  <div className="text-xs text-stone-400 mt-1">ID: {n.id}</div>
                </td>

                <td className="px-6 py-4 align-top">
                  {n.first_name ? (
                    <div>
                      <div className="text-sm font-medium">{n.first_name} {n.last_name}</div>
                      <div className="text-xs text-stone-500">{n.user_email}</div>
                    </div>
                  ) : (
                    <div className="text-sm text-stone-500">System</div>
                  )}
                </td>

                <td className="px-6 py-4 align-top">
                  <NotificationStatusBadge status={n.status} />
                </td>

                <td className="px-6 py-4 align-top text-sm text-stone-600">{formatDate(n.create_audit_time)}</td>

                <td className="px-6 py-4 text-right align-top">
                  <div className="flex items-center justify-end gap-3">
                    <button
                      title="View"
                      onClick={() => openView(n)}
                      className="p-2 rounded-full hover:bg-stone-100"
                      aria-label={`View ${n.title}`}
                    >
                      <Eye size={16} />
                    </button>
                    {/* <button
                      title="Mark read"
                      onClick={() => handleMarkRead(n)}
                      className="p-2 rounded-full hover:bg-stone-100"
                      aria-label={`Mark ${n.title} as read`}
                    >
                      <ArrowDownCircle size={16} />
                    </button> */}
                    <button
                      title="Delete"
                      onClick={() => openDelete(n)}
                      className="p-2 rounded-full hover:bg-stone-100"
                      aria-label={`Delete ${n.title}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* CARD list for small screens */}
      <div className="md:hidden space-y-3">
        {visible.length === 0 && (
          <div className="p-4 bg-white border rounded text-center text-stone-400">No notifications found.</div>
        )}

        {visible.map((n) => (
          <div key={n.id} className="bg-white border rounded-lg p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-stone-700">{n.title}</div>
                  <div className="text-xs text-stone-500">{formatDate(n.create_audit_time)}</div>
                </div>

                <div className="text-xs text-stone-500 mt-2">{n.message}</div>
                <div className="text-xs text-stone-400 mt-2">ID: {n.id}</div>

                <div className="mt-3 flex items-center gap-3">
                  <div className="min-w-[92px]">
                    {n.customer ? (
                      <div className="text-xs text-stone-600">{n.customer}</div>
                    ) : (
                      <div className="text-xs text-stone-600">System</div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <NotificationStatusBadge status={n.status} />
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-end gap-2">
                <button onClick={() => openView(n)} className="p-2 rounded-md hover:bg-stone-100" aria-label={`View ${n.title}`}>
                  <Eye size={18} />
                </button>
                {/* <button onClick={() => handleMarkRead(n)} className="p-2 rounded-md hover:bg-stone-100" aria-label={`Mark ${n.title} as read`}>
                  <ArrowDownCircle size={18} />
                </button> */}
                <button onClick={() => openDelete(n)} className="p-2 rounded-md hover:bg-stone-100" aria-label={`Delete ${n.title}`}>
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* pagination */}
      <div className="flex flex-col md:flex-row items-center justify-between mt-4 gap-3">
        <div className="text-sm text-stone-600">Showing {(page - 1) * pageSize + (visible.length ? 1 : 0)} - {(page - 1) * pageSize + visible.length} of {filtered.length}</div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Prev
          </button>
          <div className="px-3 py-1 border rounded">
            {page} / {totalPages}
          </div>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {/* Modals (unchanged behavior) */}
      <Modal
        open={viewModalOpen}
        title="Notification Details"
        onClose={() => {
          setViewModalOpen(false);
          setSelectedNotification(null);
        }}
        footer={
          <div className="flex items-center gap-2">
            {/* <button
              onClick={() => {
                if (selectedNotification) markAsRead(selectedNotification.id);
              }}
              className="px-4 py-2 bg-emerald-600 text-white rounded"
            >
              Mark as read
            </button> */}
            <button
              onClick={() => {
                setViewModalOpen(false);
                setSelectedNotification(null);
              }}
              className="px-4 py-2 border rounded bg-teal-600"
            >
              Close
            </button>
          </div>
        }
      >
        {selectedNotification ? (
          <div className="space-y-4">
            <div>
              <div className="text-sm text-stone-500">Title</div>
              <div className="font-medium text-stone-800">{selectedNotification.title}</div>
            </div>

            <div>
              <div className="text-sm text-stone-500">Message</div>
              <div className="text-sm text-stone-700">{selectedNotification.message}</div>
            </div>

            {/* <div className="grid grid-cols-2 gap-4"> */}
              <div>
                <div className="text-sm text-stone-500">ID</div>
                <div className="text-sm text-stone-700">{selectedNotification.id}</div>
              </div>
              <div>
                <div className="text-sm text-stone-500">Date</div>
                <div className="text-sm text-stone-700">{formatDate(selectedNotification.create_audit_time)}</div>
              </div>
            {/* </div> */}

            <div>
              <div className="text-sm text-stone-500">Sender / Target</div>
              {selectedNotification.first_name  ? (
                <div className="text-sm text-stone-700">{selectedNotification.first_name} {selectedNotification.last_name} — {selectedNotification.user_email}</div>
              ) : (
                <div className="text-sm text-stone-700">System</div>
              )}
            </div>

            <div>
              <div className="text-sm text-stone-500">Status</div>
              <div className="mt-2"><NotificationStatusBadge status={selectedNotification.status} /></div>
            </div>
          </div>
        ) : (
          <div className="text-center text-stone-500">No notification selected.</div>
        )}
      </Modal>

      <Modal
        open={deleteModalOpen}
        title="Delete notification"
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedNotification(null);
        }}
        footer={
          <div className="flex items-center gap-2">
            <button
              onClick={confirmDelete}
              className="px-4 py-2 bg-red-600 text-white rounded"
            >
              Delete
            </button>
            <button
              onClick={() => {
                setDeleteModalOpen(false);
                setSelectedNotification(null);
              }}
              className="px-4 py-2 border rounded"
            >
              Cancel
            </button>
          </div>
        }
      >
        <div>
          <p className="text-sm text-stone-600 mb-4">Are you sure you want to delete <strong>{selectedNotification?.title ?? "this notification"}</strong>? This action cannot be undone.</p>
        </div>
      </Modal>

      <Modal
        open={downloadModalOpen}
        title="Export Notifications"
        onClose={() => setDownloadModalOpen(false)}
        footer={
          <div className="flex items-center gap-2">
            <button
              onClick={confirmExport}
              disabled={exporting}
              className="px-4 py-2 bg-emerald-600 text-white rounded disabled:opacity-50"
            >
              {exporting ? "Exporting..." : `Export (${exportFormat.toUpperCase()})`}
            </button>
            <button onClick={() => setDownloadModalOpen(false)} className="px-4 py-2 border rounded">Cancel</button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-stone-600">Export <strong>filtered</strong> notifications (based on your current search & filters).</p>

          <div className="flex items-center gap-4">
            <label className={`px-4 py-2 border rounded cursor-pointer ${exportFormat === "csv" ? "bg-stone-100" : ""}`}>
              <input
                type="radio"
                name="exportFormat"
                value="csv"
                checked={exportFormat === "csv"}
                onChange={() => setExportFormat("csv")}
                className="mr-2"
              />
              CSV
            </label>

            <label className={`px-4 py-2 border rounded cursor-pointer ${exportFormat === "json" ? "bg-stone-100" : ""}`}>
              <input
                type="radio"
                name="exportFormat"
                value="json"
                checked={exportFormat === "json"}
                onChange={() => setExportFormat("json")}
                className="mr-2"
              />
              JSON
            </label>
          </div>

          <div className="text-xs text-stone-400">Note: The exported file will contain only the notifications currently shown by your filters.</div>
        </div>
      </Modal>
    </div>
  );
}
